import { Component, computed, inject, Input, input } from '@angular/core';
import { PlatformService } from '@examdojo/platform';
import { MarkdownModule } from 'ngx-markdown';
import { MARKDOWN_VIEWER } from './provide-markdown-viewer';

@Component({
  selector: 'dojo-markdown-viewer',
  template: `
    @if (markdownViewer) {
      <markdown
        [data]="content()"
        katex
        [katexOptions]="{ throwOnError: false, strict: 'warn' }"
        ngPreserveWhitespaces
        [class.prose]="applyProseClass"
        [class.safari-browser]="isSafari"
      />
    } @else {
      <markdown
        [data]="contentWithFixedContent()"
        katex
        ngPreserveWhitespaces
        [class.prose]="applyProseClass"
        [class.safari-browser]="isSafari"
      />
    }
  `,
  styles: `
    :host ::ng-deep markdown {
      /*render specific UI for different types of markdown blocks*/
      .example-block {
        border-radius: 1rem;
        padding: theme('spacing.4');
        border: 1px solid;
        font-size: theme('fontSize.sm.0');
        line-height: theme('fontSize.sm.1');
        margin-bottom: theme('spacing.3');

        ol,
        ul,
        menu {
          padding-left: 20px;
        }

        ul {
          list-style-type: disc;
        }

        ol {
          list-style-type: demical;
        }

        > div {
          &::before {
            display: flex;
            --icon-size: theme('spacing.4');
            padding-left: calc(#{var(--icon-size)} + theme('spacing.2'));
            background-size: var(--icon-size) var(--icon-size);
            background-repeat: no-repeat;
            background-position: left center;
            margin-bottom: theme('spacing.3');
            font-weight: 600;
            font-size: theme('fontSize.xs.0');
            line-height: theme('fontSize.xs.1');
          }
        }

        &.warning {
          border-color: theme('colors.amber.300');
          background: theme('colors.amber.50');
          color: theme('colors.amber.700');

          > div {
            &::before {
              content: 'Warning';
              background-image: url('/assets/images/callout/warning-icon.svg');
            }
          }
        }

        &.note {
          border-color: theme('colors.sky.300');
          background: theme('colors.sky.50');
          color: theme('colors.sky.700');

          > div {
            &::before {
              content: 'Note';
              background-image: url('/assets/images/callout/note-icon.svg');
            }
          }
        }

        &.tip {
          border-color: theme('colors.purple.300');
          background: theme('colors.purple.50');
          color: theme('colors.purple.700');

          > div {
            &::before {
              content: 'Tip';
              background-image: url('/assets/images/callout/tip-icon.svg');
            }
          }
        }

        &.example {
          border-color: theme('colors.green.300');
          background: theme('colors.green.50');
          color: theme('colors.green.700');

          > div {
            &::before {
              content: 'Example';
              background-image: url('/assets/images/callout/example-icon.svg');
            }
          }
        }
      }

      table {
        // Ensure all table borders use neutral-200
        th,
        td {
          border-color: theme('colors.neutral.200') !important;
        }

        &.table-header-top-center {
          thead th {
            text-align: center;
          }
        }

        &.table-cell-center {
          td {
            text-align: center;
          }
        }

        &.table-header-top-left {
          thead th {
            text-align: left;
          }
        }

        &.table-cell-left {
          td {
            text-align: left;
          }
        }

        &.table-header-top-right {
          thead th {
            text-align: right;
          }
        }

        &.table-cell-right {
          td {
            text-align: right;
          }
        }

        .vertical-text-lr,
        .vertical-text-rl {
          text-align: center;
          vertical-align: middle;
          max-height: 150px;
        }

        /* Default for all browsers except Safari */
        @supports (writing-mode: sideways-lr) {
          .vertical-text-lr {
            transform: none;
            writing-mode: sideways-lr;
            text-orientation: mixed;
          }

          .vertical-text-rl {
            transform: none;
            writing-mode: sideways-rl;
            text-orientation: mixed;
          }
        }

        .remove-cell-border {
          border: 0;
        }

        &.equal-col-width {
          table-layout: fixed;
        }

        td.align-cell-left,
        th.align-cell-left {
          text-align: left;
        }

        td.align-cell-center,
        th.align-cell-center {
          text-align: center;
        }

        td.align-cell-right,
        th.align-cell-right {
          text-align: right;
        }

        &.mcq-options {
          thead,
          tr {
            border-color: var(--neutral-200);
          }

          tr::before {
            content: attr(label);
            font-weight: 400;
            padding-right: 1rem;
            display: block;
            text-align: right;
          }

          tbody tr:last-child {
            border-bottom: 1px solid var(--neutral-200);
          }
        }
      }

      /* Safari hack */
      &.safari-browser table {
        .vertical-text-lr {
          transform: rotate(90deg);
          writing-mode: unset;
        }

        .vertical-text-rl {
          transform: rotate(-90deg);
          writing-mode: unset;
        }
      }

      /*Remove top margin from first KaTeX display element for better alignment with stem labels*/
      .katex-display:first-of-type {
        margin-top: 0;
      }

      p {
        line-height: 1.5;
      }
    }
  `,
  imports: [MarkdownModule],
  host: { class: 'max-w-full' },
})
export class MarkdownViewerComponent {
  constructor(private readonly platformService: PlatformService) {}

  readonly content = input.required<string>();
  readonly markdownViewer = inject(MARKDOWN_VIEWER, { optional: true });

  readonly katexEnvsRegexp =
    /(\\begin\{(?:p?matrix|smallmatrix|vmatrix|Bmatrix|bmatrix|aligned|cases)\}[\s\S]*?\\end\{(?:p?matrix|smallmatrix|vmatrix|Bmatrix|bmatrix|aligned|cases)\})/g;

  readonly contentWithFixedContent = computed(() => {
    const content = this.content();

    // https://linear.app/tm007/issue/FE-504
    const fixedLatexContent = content.replace(/\$(.*?)\$/gs, (match, p1) => `$${p1.replaceAll('\\%', '\\\\%')}$`);

    // https://linear.app/tm007/issue/FE-381
    return fixedLatexContent.replaceAll(
      this.katexEnvsRegexp,
      // Replace 2 backslashes with 4 backslashes for the matrix line separation.
      (match, p1) => `${p1.replaceAll('\\\\', '\\\\\\\\')}`,
    );
  });

  readonly isSafari = this.platformService.isSafari;

  @Input() applyProseClass?: boolean = true;
}
